import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { MapPin, Star, BadgeCheck, Clock, Phone, Heart, MessageCircle, ChevronLeft } from "lucide-react";

export default function ProviderProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ categories: [] });
  const [favorited, setFavorited] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/providers/${id}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));

  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data));
  }, [id]);

  useEffect(() => {
    if (user) api.get(`/favorites/${id}/check`).then((r) => setFavorited(r.data.favorited)).catch(() => {});
  }, [user, id]);

  if (!data) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-muted-foreground">Carregando...</div>;
  if (data.error) return <div className="max-w-5xl mx-auto px-6 py-20 text-center">Prestador não encontrado.</div>;

  const { provider: p, reviews } = data;
  const cat = meta.categories.find((c) => c.id === p.category);

  const whatsapp = () => {
    api.post(`/providers/${id}/lead`).catch(() => {});
    window.open(`https://wa.me/${p.whatsapp}?text=${encodeURIComponent(`Olá! Encontrei ${p.business_name} no PetMachine Hub e gostaria de saber mais.`)}`, "_blank");
  };

  const toggleFav = async () => {
    if (!user) return toast.error("Entre na sua conta para favoritar");
    const r = await api.post(`/favorites/${id}`);
    setFavorited(r.data.favorited);
    toast.success(r.data.favorited ? "Adicionado aos favoritos" : "Removido dos favoritos");
  };

  const submitReview = async () => {
    if (!user) return toast.error("Entre na sua conta para avaliar");
    setSending(true);
    try {
      await api.post(`/providers/${id}/reviews`, { rating, comment });
      toast.success("Avaliação enviada! Obrigado.");
      setComment("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar avaliação");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <Link to="/prestadores" className="inline-flex items-center text-sm text-muted-foreground hover:text-brand-coral mb-5" data-testid="back-to-directory">
        <ChevronLeft className="w-4 h-4" /> Voltar ao diretório
      </Link>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="rounded-3xl overflow-hidden h-72 bg-brand-mintLight">
            {p.photos?.[0] && <img src={photoUrl(p.photos[0])} alt={p.business_name} className="w-full h-full object-cover" />}
          </div>
          {p.photos?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {p.photos.slice(1, 5).map((ph, i) => (
                <img key={i} src={photoUrl(ph)} alt="" className="rounded-xl h-20 w-full object-cover" />
              ))}
            </div>
          )}

          <h2 className="font-heading font-bold text-xl text-brand-graphite mt-8 mb-3">Serviços e preços</h2>
          <div className="bg-white rounded-2xl border border-brand-coral/10 divide-y" data-testid="services-list">
            {p.services?.length ? p.services.map((s, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3.5">
                <span className="text-sm text-brand-graphite">{s.name}</span>
                <span className="font-heading font-semibold text-brand-coral">R$ {Number(s.price).toFixed(2).replace(".", ",")}</span>
              </div>
            )) : <p className="px-5 py-4 text-sm text-muted-foreground">Serviços sob consulta.</p>}
          </div>

          <h2 className="font-heading font-bold text-xl text-brand-graphite mt-10 mb-3">Avaliações ({reviews.length})</h2>
          <div className="space-y-4" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.review_id} className="bg-white rounded-2xl p-5 border border-brand-coral/5">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-brand-graphite">{r.tutor_name}</p>
                  <span className="flex items-center gap-1 text-sm">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-brand-mustard text-brand-mustard" : "text-gray-200"}`} />
                    ))}
                  </span>
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
              </div>
            ))}
            {reviews.length === 0 && <p className="text-sm text-muted-foreground">Ainda sem avaliações. Seja o primeiro!</p>}
          </div>

          <div className="bg-white rounded-2xl p-5 border border-brand-coral/10 mt-6" data-testid="review-form">
            <p className="font-heading font-semibold text-brand-graphite mb-3">Avaliar este prestador</p>
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
                  <Star className={`w-6 h-6 ${n <= rating ? "fill-brand-mustard text-brand-mustard" : "text-gray-300"}`} />
                </button>
              ))}
            </div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Conte como foi sua experiência..." data-testid="review-comment-input" />
            <Button onClick={submitReview} disabled={sending} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full mt-3" data-testid="review-submit-btn">
              Enviar avaliação
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl p-6 border border-brand-coral/10 sticky top-24 shadow-sm" data-testid="provider-sidebar">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <h1 className="font-heading font-bold text-2xl text-brand-graphite leading-tight">{p.business_name}</h1>
                  {p.verified && <BadgeCheck className="w-5 h-5 text-brand-mint shrink-0" data-testid="profile-verified-badge" />}
                </div>
                <p className="text-xs font-semibold text-brand-coral uppercase tracking-wide mt-1">{cat?.name}</p>
              </div>
              <button onClick={toggleFav} data-testid="favorite-toggle-btn" className="p-2 rounded-full hover:bg-brand-coralLight transition-colors">
                <Heart className={`w-6 h-6 ${favorited ? "fill-brand-coral text-brand-coral" : "text-gray-300"}`} />
              </button>
            </div>

            <div className="flex items-center gap-1.5 mt-3">
              <Star className="w-5 h-5 fill-brand-mustard text-brand-mustard" />
              <span className="font-heading font-bold text-lg" data-testid="profile-rating">{p.rating_avg > 0 ? p.rating_avg : "—"}</span>
              <span className="text-sm text-muted-foreground">({p.rating_count} avaliações)</span>
            </div>

            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{p.description}</p>

            <div className="space-y-2.5 mt-5 text-sm">
              <p className="flex items-center gap-2 text-brand-graphite"><MapPin className="w-4 h-4 text-brand-mint" /> {p.address || p.city}</p>
              <p className="flex items-center gap-2 text-brand-graphite"><Clock className="w-4 h-4 text-brand-mint" /> {p.hours || "Horário sob consulta"}</p>
              {p.phone && <p className="flex items-center gap-2 text-brand-graphite"><Phone className="w-4 h-4 text-brand-mint" /> {p.phone}</p>}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {p.neighborhoods?.map((n) => <Badge key={n} variant="secondary" className="bg-brand-mintLight text-brand-mintDark text-xs">{n}</Badge>)}
              </div>
            </div>

            <Button onClick={whatsapp} className="w-full bg-[#25D366] hover:bg-[#1fb857] text-white rounded-full mt-6 h-12 font-semibold" data-testid="whatsapp-contact-btn">
              <MessageCircle className="w-5 h-5 mr-2" /> Chamar no WhatsApp
            </Button>
            {p.category === "banho_automatico" && (
              <Link to="/planos" className="block" data-testid="bath-booking-link">
                <Button variant="outline" className="w-full rounded-full mt-3 h-11 border-brand-coral text-brand-coral hover:bg-brand-coralLight">
                  Reservar banho na máquina (R$ 69,90)
                </Button>
              </Link>
            )}
            {p.address && !p.address.toLowerCase().includes("domicílio") && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address + ", Joinville - SC")}`} target="_blank" rel="noreferrer" className="block" data-testid="how-to-get-btn">
                <Button variant="outline" className="w-full rounded-full mt-3 h-11 border-brand-mint text-brand-mintDark hover:bg-brand-mintLight">
                  <MapPin className="w-4 h-4 mr-2" /> Como chegar
                </Button>
              </a>
            )}
            {p.category === "banho_automatico" && (
              <a href="https://petmachine.ind.br" target="_blank" rel="noreferrer" className="block mt-3 text-center text-xs font-semibold text-brand-coral underline underline-offset-2" data-testid="partner-machine-link">
                Seja parceiro: compre a sua máquina e coloque no seu ponto estratégico
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
